/* eslint-disable */

import React, { useState, useEffect } from "react";
import { Card, Box, Drawer, CircularProgress, Icon } from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

import ApplyLeaveForm from "../applyleaveform";
import LeaveRecord from "../leaverecord";

import EventBusyIcon from "@mui/icons-material/EventBusy";
import { usersVisibleLeavePolicy } from "api/users";
import PropTypes from "prop-types";
import { useAuth } from "AuthContext";
import { hasPermission } from "permissionUtils";
import FlagLoader from "layouts/flagloader";

// ------------------------
// LEAVE CONFIG
// ------------------------
const leaveTypeConfig = {
  CL: { type: "Casual Leave", icon: "beach_access", bg: "#e7f3ff", color: "#1976d2" },
  SL: { type: "Sick Leave", icon: "local_hospital", bg: "#e8f5e9", color: "#2e7d32" },
  PL: { type: "Paternity Leave", icon: "child_friendly", bg: "#fdecef", color: "#c2185b" },
  ML: { type: "Maternity Leave", icon: "pregnant_woman", bg: "#fff8e1", color: "#f9a825" },
  NL: { type: "Negative Leave", icon: "warning", bg: "#f3e5f5", color: "#6a1b9a" },
};

function LeaveBalance({ userId = null, year = 2025 }) {
  const { userPermission } = useAuth();
  const canApplyMyLeave = hasPermission(
    "LeaveTracker.MyData.ApplyButton",
    userPermission
  );
  const actualUserId = userId || localStorage.getItem("loggedUserId");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const [leaveData, setLeaveData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --------------------------
  // FETCH DATA
  // --------------------------
  useEffect(() => {
    const fetchLeaves = async () => {
      try {
        setLoading(true);

        const res = await usersVisibleLeavePolicy(actualUserId);
        const raw = res || [];

        const mapped = raw.map((item) => {
          const cfg = leaveTypeConfig[item.leave_code] || {
            type: item.leave_name,
            icon: "help",
            bg: "#eee",
            color: "#555",
          };

          return {
            id: item.id,
            type: cfg.type,
            icon: cfg.icon,
            bg: cfg.bg,
            color: cfg.color,
            available: item.stats?.available ?? 0,
            booked: item.stats?.booked ?? 0,
            // remaining: (item.stats?.total ?? 0) - (item.stats?.booked ?? 0),
            remaining: (item.stats?.remaining ?? 0),
            total: item.stats?.total ?? 0,
            leaveCode: item.leave_code,
          };
        });

        setLeaveData(mapped);
      } catch (err) {
        setError("Failed to load leave balances");
      } finally {
        setLoading(false);
      }
    };

    fetchLeaves();
  }, [actualUserId, year]);

  if (loading) {
    return (
      <SoftBox p={3} textAlign="center">
        {/* <CircularProgress size={45} /> */}
        <FlagLoader />
      </SoftBox>
    );
  }

  // --------------------------
  // ERROR UI
  // --------------------------
  if (error) {
    return (
      <SoftBox p={3} textAlign="center">
        <Card sx={{ p: 4 }}>
          <Icon sx={{ fontSize: 60, color: "error.main" }}>error</Icon>
          <SoftTypography variant="h5" color="error" mt={2}>
            {error}
          </SoftTypography>
        </Card>
      </SoftBox>
    );
  }

  // --------------------------
  // EMPTY UI
  // --------------------------
  if (!leaveData.length) {
    return (
      <Card>
        <SoftBox textAlign="center" p={3}>
          <EventBusyIcon sx={{ fontSize: 64, color: "text.secondary" }} />
          <SoftTypography variant="h6" color="text">
            No Leave Balances Available
          </SoftTypography>
        </SoftBox>
      </Card>
    );
  }

  return (
    <Card>
      <SoftBox p={3}>
        <SoftTypography variant="h6" fontWeight="semibold" mb={1} color="text">
          My Leave Balances
        </SoftTypography>
        <SoftTypography variant="body2" mb={3}>
          Overview of your leaves for {year}
        </SoftTypography>

        {/* ============= CLEAN SINGLE-ROW CARD ============= */}
        {leaveData.map((leave, index) => (
          <Card
            key={leave.id}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 3,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexDirection: "row",
              flexWrap: "nowrap !important", // ⛔ STOP WRAPPING
              gap: 4,
              transition: "0.25s",
              "&:hover": {
                boxShadow: 6,
                transform: "translateY(-2px)",
              },
            }}
          >
            {/* LEFT SECTION */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                gap: 2,
                minWidth: "260px",
              }}
            >
              <SoftBox
                width="50px"
                height="50px"
                borderRadius="lg"
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                  backgroundColor: leave.bg,
                  transition: "0.25s",
                }}
              >
                <Icon sx={{ fontSize: 26, color: leave.color }}>{leave.icon}</Icon>
              </SoftBox>

              <Box>
                <SoftTypography variant="h6" fontWeight="bold">
                  {leave.type}
                </SoftTypography>
                <SoftTypography variant="caption" color="text">
                  Code: {leave.leaveCode}
                </SoftTypography>
              </Box>
            </Box>

            {/* CENTER STATS */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-evenly",
                flex: 1,
                flexDirection: "row",
                gap: 8,
                flexShrink: 0,
                whiteSpace: "nowrap", // ⛔ STOP TEXT WRAP
              }}
            >
              {[
                {
                  label: "Available",
                  value: leave.available,
                  color: leave.available > 0 ? "success.main" : "error.main",
                },

                { label: "Booked", value: leave.booked, color: "text.primary" },
                {
                  label: "Remaining",
                  value: leave.remaining,
                  color: leave.remaining > 0 ? "success.main" : "error.main",
                },
                { label: "Total", value: leave.total, color: "info.main" },
              ].map((stat, i) => (
                <Box key={i} textAlign="center" minWidth="80px">
                  <SoftTypography variant="button" fontWeight="bold" color="text">
                    {stat.label}
                  </SoftTypography>
                  <SoftTypography variant="h6" color="text">
                    {stat.value}
                  </SoftTypography>
                </Box>
              ))}
            </Box>

            {/* APPLY BUTTON */}
            {canApplyMyLeave &&
              <SoftButton
                size="small"
                color="info"
                variant="outlined"
                sx={{
                  flexShrink: 0, // ⛔ Never push button down
                  whiteSpace: "nowrap",
                  minWidth: "90px",
                  textAlign: "center",
                }}
                onClick={() => {
                  setSelectedLeaveType(leave.type);
                  setDrawerOpen(true);
                }}
              >
                APPLY
              </SoftButton>
            }
          </Card>
        ))}

        {/* DRAWER */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          hideBackdrop
          PaperProps={{
            sx: {
              width: "90vw",
              maxWidth: "90vw",
            },
          }}
        >
          <SoftBox p={3} display="flex" gap={3}>
            <ApplyLeaveForm
              selectedLeaveType={selectedLeaveType}
              handleClose={() => setDrawerOpen(false)}
              onDateChange={setSelectedDate}
            />

            {selectedDate && (
              <SoftBox width="45%">
                <LeaveRecord selectedDate={selectedDate} />
              </SoftBox>
            )}
          </SoftBox>
        </Drawer>
      </SoftBox>
    </Card>
  );
}

LeaveBalance.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  year: PropTypes.number,
};

export default LeaveBalance;
